import {
  GridArrowDownwardIcon,
  GridArrowUpwardIcon,
  gridColumnDefinitionsSelector,
  gridSortModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { Sort } from '@mui/icons-material';

interface ToolbarSortItemProps {
  listView: boolean;
}

export function ToolbarSortItem({ listView }: ToolbarSortItemProps) {
  const [open, setOpen] = useState<boolean>(false);
  const apiRef = useGridApiContext();
  const fields = useGridSelector(apiRef, gridColumnDefinitionsSelector);
  const sortModel = useGridSelector(apiRef, gridSortModelSelector);
  const sortableFields = fields.filter((field) => field.sortable);
  const sortCount = sortModel.length;
  const { t } = useTranslation();

  const handleSortChange = (field: string) => {
    const index = sortModel.findIndex((item) => item.field === field);
    const newSortModel = [...sortModel];

    if (index === -1) {
      newSortModel.push({ field, sort: 'asc' });
    } else {
      const currentSort = sortModel[index].sort;
      if (currentSort === 'asc') {
        newSortModel[index] = { field, sort: 'desc' };
      } else {
        newSortModel.splice(index, 1);
      }
    }

    apiRef.current.setSortModel(newSortModel);
  };

  return (
    <>
      <IconButton
        data-qa-element-id="sort_button"
        onClick={() => setOpen(true)}
        sx={{
          color: 'primary.main',
          alignItems: 'center',
          width: '64px',
        }}
      >
        <Badge badgeContent={sortCount} color={sortCount > 0 ? 'primary' : 'default'}>
          <Sort />
        </Badge>
      </IconButton>
      <Drawer
        anchor={'bottom'}
        open={open}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        slotProps={{
          root: {
            sx: {
              zIndex: '1300',
            },
          },
          paper: {
            sx: {
              paddingBottom: '50px',
              overflowY: 'auto',
            },
          },
        }}
      >
        <div data-qa-element-id="sort_order_panel" style={{ width: listView ? 'auto' : 300, padding: 16 }}>
          <Typography data-qa-element-id="sort_order_panel_title" variant="h6" gutterBottom fontWeight={500}>
            {t('refuels.sortBy')}
          </Typography>

          <List>
            {sortableFields.map((field) => {
              const sortEntry = sortModel.find((sort) => sort.field === field.field);
              const sortDirection = sortEntry?.sort;

              const isAnotherFieldSorted = sortModel.length > 0 && sortModel[0].field !== field.field;

              return (
                <ListItem data-qa-element-id={`sort_by_${field.field}`} key={field.field} disablePadding>
                  <ListItemButton onClick={() => handleSortChange(field.field)} disabled={isAnotherFieldSorted}>
                    <ListItemIcon data-qa-element-id="sort_order_icon">
                      {sortDirection === 'asc' && <GridArrowUpwardIcon />}
                      {sortDirection === 'desc' && <GridArrowDownwardIcon />}
                    </ListItemIcon>
                    <ListItemText>{field.headerName || field.field}</ListItemText>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </div>
      </Drawer>
    </>
  );
}
